import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, ArrowDown } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal, RevealLines } from "@/components/common/Reveal";
import { Counter } from "@/components/common/Counter";
import { Icon } from "@/components/common/Icon";
import { SERVICES } from "@/data/services";
import { TECHNOLOGIES } from "@/data/technologies";
import api from "@/lib/api";

export default function Home() {
  const { lang, t, ml } = useI18n();
  const L = (p) => `/${lang}${p}`;
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const heroRef = useRef(null);

  useSEO({
    lang,
    title: "HO-SYS | " + t("hero.titleLines").join(" "),
    description: t("hero.subtitle"),
    path: "",
    image: "https://images.unsplash.com/photo-1579567761406-4684ee0c75b6?q=85&w=1200",
  });

  useEffect(() => {
    api.get("/projects", { params: { featured: true } }).then((r) => setProjects(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const statItems = stats ? [
    { value: stats.projects, suffix: "+", key: "projects" },
    { value: stats.clients, suffix: "+", key: "clients" },
    { value: stats.apps, suffix: "+", key: "apps" },
    { value: stats.users, suffix: "+", key: "users" },
    { value: stats.municipalities, suffix: "", key: "municipalities" },
    { value: stats.years, suffix: "+", key: "years" },
  ] : [];

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-center overflow-hidden pt-28 pb-16">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <motion.div
          style={{ y: glowY }}
          className="absolute -top-40 -right-32 w-[680px] h-[680px] rounded-full bg-neon/15 blur-[140px] pointer-events-none"
        />
        <motion.div
          style={{ y: glowY }}
          className="absolute top-1/3 -left-40 w-[420px] h-[420px] rounded-full bg-lime/10 blur-[120px] pointer-events-none"
        />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />

        <motion.div style={{ y: heroTextY, opacity: heroOpacity }} className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-lime animate-pulse" />
            <span className="font-mono text-xs uppercase tracking-widest text-white/70">{t("hero.badge")}</span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tighter text-white text-[clamp(2.4rem,7vw,6rem)] leading-[0.95]">
            <RevealLines
              lines={t("hero.titleLines")}
              lineClassName=""
              start={0.15}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 max-w-2xl text-lg md:text-xl text-white/60 leading-relaxed"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link to={L("/projects")} data-testid="hero-cta-projects"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-neon px-7 py-4 text-base font-semibold text-black hover:bg-white transition-colors">
              {t("hero.cta1")}
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to={L("/contact")} data-testid="hero-cta-demo"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-4 text-base font-semibold text-white hover:border-neon/60 hover:text-neon transition-colors">
              {t("hero.cta2")}
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
          <span className="font-mono text-[10px] uppercase tracking-widest">{t("hero.scroll")}</span>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
            <ArrowDown className="w-4 h-4" />
          </motion.div>
        </motion.div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="py-10 border-y border-white/10 bg-black">
        <p className="text-center font-mono text-xs uppercase tracking-widest text-white/40 mb-6">{t("clients.label")}</p>
        <Marquee speed={40} gradient gradientColor="#000000" gradientWidth={80}>
          {["ReusApp", "Observatori Reus", "TapaReus", "Flaps", "HO-SYS TPV", "HO-SYS Normatives", "APAMS", "Arrosganxet", "Arrosmariner", "D'Gusta"].map((c, i) => (
            <span key={i} className="mx-8 font-display text-2xl font-semibold text-white/30 hover:text-white/70 transition-colors">{c}</span>
          ))}
        </Marquee>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <SectionLabel>{t("servicesSection.label")}</SectionLabel>
            <h2 className="mt-4 font-display font-bold tracking-tight text-white text-4xl sm:text-5xl">{t("servicesSection.title")}</h2>
            <p className="mt-4 text-white/55 text-lg">{t("servicesSection.subtitle")}</p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 md:gap-5">
            {SERVICES.map((s, i) => {
              const span = i === 0 ? "md:col-span-3" : i === 1 ? "md:col-span-3" : "md:col-span-2";
              return (
                <Reveal key={s.slug} delay={i * 0.05} className={span}>
                  <Link to={L(`/services/${s.slug}`)} data-testid={`service-card-${s.slug}`}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      className="group h-full rounded-2xl border border-white/10 bg-[#0A0A0A] p-7 hover:border-neon/40 transition-colors relative overflow-hidden"
                    >
                      <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-neon/5 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="w-12 h-12 rounded-xl bg-neon/10 border border-neon/25 flex items-center justify-center mb-6">
                        <Icon name={s.icon} className="w-6 h-6 text-neon" />
                      </div>
                      <h3 className="font-display text-xl font-semibold text-white mb-2">{ml(s.title)}</h3>
                      <p className="text-white/50 text-sm leading-relaxed">{ml(s.short)}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-neon opacity-0 group-hover:opacity-100 transition-opacity">
                        {t("common.explore")} <ArrowRight className="w-4 h-4" />
                      </span>
                    </motion.div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="py-24 md:py-32 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <SectionLabel>{t("featured.label")}</SectionLabel>
              <h2 className="mt-4 font-display font-bold tracking-tight text-white text-4xl sm:text-5xl">{t("featured.title")}</h2>
              <p className="mt-4 text-white/55 text-lg">{t("featured.subtitle")}</p>
            </div>
            <Link to={L("/projects")} data-testid="featured-view-all"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-neon transition-colors">
              {t("featured.cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {projects.map((p, i) => (
              <Reveal key={p.id} delay={(i % 2) * 0.08}>
                <Link to={L(`/projects/${p.id}`)} data-testid={`project-card-${p.id}`}>
                  <motion.div whileHover={{ y: -5 }} className="group rounded-2xl overflow-hidden border border-white/10 bg-[#0A0A0A] hover:border-neon/40 transition-colors">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={p.cover} alt={p.name} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                      <span className="absolute top-4 left-4 rounded-full bg-black/70 backdrop-blur px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-neon border border-neon/30">
                        {ml(p.category)}
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <h3 className="font-display text-2xl font-semibold text-white">{p.name}</h3>
                        <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-neon transition-colors" />
                      </div>
                      <p className="mt-2 text-white/50 text-sm">{ml(p.tagline)}</p>
                    </div>
                  </motion.div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <section className="py-16 bg-black overflow-hidden border-y border-white/10">
        <Marquee speed={30} gradient={false}>
          {t("marquee").map((w, i) => (
            <span key={i} className="mx-6 font-display text-6xl md:text-8xl font-black text-stroke uppercase">{w}</span>
          ))}
        </Marquee>
      </section>

      {/* STATS */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <SectionLabel>{t("stats.label")}</SectionLabel>
            <h2 className="mt-4 font-display font-bold tracking-tight text-white text-4xl sm:text-5xl">{t("stats.title")}</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {statItems.map((s, i) => (
              <div key={s.key} data-testid={`stat-${s.key}`} className="bg-[#0A0A0A] p-8 md:p-10">
                <div className="font-display text-4xl md:text-6xl font-extrabold text-white tracking-tight">
                  <Counter value={s.value} suffix={s.suffix} />
                </div>
                <p className="mt-2 text-white/50 text-sm">{t(`stats.items.${s.key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGIES */}
      <section className="py-24 md:py-32 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <SectionLabel>{t("tech.label")}</SectionLabel>
            <h2 className="mt-4 font-display font-bold tracking-tight text-white text-4xl sm:text-5xl">{t("tech.title")}</h2>
            <p className="mt-4 text-white/55 text-lg">{t("tech.subtitle")}</p>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {TECHNOLOGIES.map((tech, i) => (
              <Reveal key={tech.name} delay={i * 0.02}>
                <div className="group inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-[#0A0A0A] px-5 py-3 hover:border-neon/40 transition-colors">
                  <Icon name={tech.icon} className="w-4 h-4 text-neon" />
                  <span className="text-sm text-white/80 group-hover:text-white transition-colors">{tech.name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY HO-SYS MANIFESTO */}
      <section className="py-24 md:py-32 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-3xl mb-16">
            <SectionLabel>{t("why.label")}</SectionLabel>
            <h2 className="mt-4 font-display font-bold tracking-tighter text-white text-4xl sm:text-6xl leading-[1.05]">{t("why.title")}</h2>
            <p className="mt-5 text-white/55 text-lg">{t("why.subtitle")}</p>
          </Reveal>
          <div className="space-y-px">
            {t("why.chapters").map((c, i) => (
              <Reveal key={c.n} delay={i * 0.06}>
                <div className="group grid grid-cols-1 md:grid-cols-12 gap-6 items-start py-8 border-t border-white/10 hover:bg-white/[0.02] transition-colors">
                  <div className="md:col-span-2 font-display text-5xl font-black text-white/15 group-hover:text-neon transition-colors">{c.n}</div>
                  <h3 className="md:col-span-4 font-display text-2xl font-semibold text-white">{c.title}</h3>
                  <p className="md:col-span-6 text-white/55 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}

export function SectionLabel({ children }) {
  return (
    <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-neon">
      <span className="w-6 h-px bg-neon" />{children}
    </span>
  );
}

export function CtaBanner() {
  const { lang, t } = useI18n();
  const L = (p) => `/${lang}${p}`;
  return (
    <section className="py-24 md:py-32 bg-black border-t border-white/10">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal className="relative rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden p-10 md:p-16 text-center">
          <div className="absolute inset-0 grid-bg opacity-40" />
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-neon/15 blur-[120px] rounded-full" />
          <div className="relative">
            <h2 className="font-display font-bold tracking-tight text-white text-3xl sm:text-5xl">{t("cta.title")}</h2>
            <p className="mt-4 text-white/60 text-lg max-w-xl mx-auto">{t("cta.subtitle")}</p>
            <Link to={L("/contact")} data-testid="cta-banner-button"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-neon px-8 py-4 text-base font-semibold text-black hover:bg-white transition-colors">
              {t("cta.button")} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
